import { breakpoints, Button, Icon, Input, InputProps, Theme, useBelowBreakpoint } from 'imaginarity-react-ui';
import * as React from 'react';
import { ThemeContext } from 'styled-components';
import { DurationInputSC as T } from './DurationInputSC';

interface DurationInputGetHoursProps {
    durationFromInput: (value: number) => void;
    value?: number;
    minimized?: boolean;
    maximized?: boolean;
    title?: string;
    cleareable?: boolean;
    info?: string;
    error?: boolean;
    shortenText?: boolean;
    debounce?: InputProps["debounce"];
}

type InputTime = { d: number, h: number };

const hpd = 24;

const calcNewValue = (cur: InputTime) => {
    if (cur.h >= hpd) {
        cur.d += Math.floor(cur.h / hpd);
        cur.h = cur.h % hpd;
    }
    if (cur.h < 0) {
        if (cur.d > 0) {
            cur.h = hpd + cur.h;
            cur.d--;
        }
        else
            cur.h = 0;
    }
    if (cur.d < 0)
        cur.d = 0;
    return cur;
}

const calcHours = (val: InputTime) => {
    return (val.d * 24 + val.h);
}

const DurationInputGetHours = (p: DurationInputGetHoursProps) => {
    const { durationFromInput, minimized, maximized,
        cleareable, title, info, error, shortenText,
        value, debounce } = p;
    const theme = React.useContext<Theme>(ThemeContext);

    // Initialisieren des Zustands basierend auf der `value`-Prop
    const [inputState, setInputState] = React.useState<InputTime>(() => {
        const days = value ? Math.floor(value / 24) : 0;
        const hours = value ? (value - (24 * days)) : 0;
        return { d: days, h: hours };
    });

    // Effekt, um `inputState` zu aktualisieren, wenn sich `value` ändert
    React.useEffect(() => {
        const days = value ? Math.floor(value / 24) : 0;
        const hours = value ? (value - (24 * days)) : 0;
        setInputState({ d: days, h: hours });
    }, [value]);

    // Entfernen des Debounce für sofortige Aktualisierungen
    const update = durationFromInput;

    const { onChangeDays, onChangeHours, onClear, countIcon } = React.useMemo(() => ({
        onChangeDays: (val: string | undefined) => {
            setInputState(i => {
                const x = parseInt(val ?? "0", 10);
                const next = calcNewValue({ ...i, d: Number.isNaN(x) ? 0 : x });
                update(calcHours(next));
                return next;
            });
        },
        onChangeHours: (val: string | undefined) => {
            setInputState(i => {
                const x = parseInt(val ?? "0", 10);
                const next = calcNewValue({ ...i, h: Number.isNaN(x) ? 0 : x });
                update(calcHours(next));
                return next;
            });
        },
        onClear: () => {
            const next: InputTime = { d: 0, h: 0 };
            setInputState(next);
            update(calcHours(next));
        },
        countIcon: (ico: "add" | "minus") => {
            if (ico === "add") {
                const icon = minimized ? "add" : "chevron up";
                return <Icon name={icon} size="11px" marginTop={0} />;
            }
            if (ico === "minus") {
                const icon = minimized ? "minus" : "chevron down";
                return <Icon name={icon} size="11px" marginTop={0} />;
            }
        },
    }), [update, minimized]);

    const smallDevice = minimized || theme.content.width < breakpoints.tablet.width || shortenText;
    const miniDevice = useBelowBreakpoint("tablet");
    const titleMode = (title && info) ? "both" : (title && !info) ? "title" : (!title && info) ? "info" : undefined;

    const modify = React.useMemo(() => (curValue: number, changeCall: (val: string | undefined) => void, modifyBy: number = 1) => (ev: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (ev.key === "ArrowUp")
            changeCall(`${curValue + modifyBy}`);
        if (ev.key === "ArrowDown")
            changeCall(`${curValue - modifyBy}`);
    }, []);

    const modifyBtn = React.useMemo(() => (curValue: number, changeCall: (val: string | undefined) => void, modifyBy: number = 1, click: "up" | "down") => () => {
        if (click === "up")
            changeCall(`${curValue + modifyBy}`);
        if (click === "down")
            changeCall(`${curValue - modifyBy}`);
    }, []);

    const isMinimized = minimized && !miniDevice;
    const isMaximized = maximized || miniDevice;

    return (
        <div>
            {(title || info) &&
                <T.Title minimized={isMinimized ?? false} maximized={isMaximized ?? false} cleareable={cleareable ?? false} titleMode={titleMode} >
                    {title && <T.TitleText title={title}>{title}</T.TitleText>}
                    {info && <T.TitleInfo title={info} hasError={error}>{info}</T.TitleInfo>}
                </T.Title>
            }
            <T.Container maximized={isMaximized ?? false} minimized={isMinimized ?? false} cleareable={cleareable ?? false} miniDevice={miniDevice} error={error ?? false} noMinutes={true}>

                <T.Column miniDevice={miniDevice}>
                    <Input
                        kind='defaultTransparent'
                        maxLength={3}
                        value={inputState.d}
                        change={onChangeDays}
                        labelInside={miniDevice ? "days" : undefined}
                        onKeyDown={modify(inputState.d, onChangeDays)}
                        debounce={debounce}
                    />
                    {!miniDevice && !isMinimized && <T.Unit unit={smallDevice ? "d" : "days"} />}
                    {isMinimized &&
                        <T.UnitMinimized>days</T.UnitMinimized>
                    }
                    <T.CountUp onClick={modifyBtn(inputState.d, onChangeDays, 1, "up")} minimized={isMinimized}>{countIcon("add")}</T.CountUp>
                    <T.CountDown onClick={modifyBtn(inputState.d, onChangeDays, 1, "down")} minimized={isMinimized}>{countIcon("minus")}</T.CountDown>
                </T.Column>

                <T.Column miniDevice={miniDevice}>
                    <Input
                        kind='defaultTransparent'
                        maxLength={2}
                        value={inputState.h}
                        change={onChangeHours}
                        labelInside={miniDevice ? "hours" : undefined}
                        onKeyDown={modify(inputState.h, onChangeHours)}
                        min={0}
                        max={24}
                        debounce={debounce}

                    />
                    {!miniDevice && !isMinimized &&
                        <T.Unit unit={shortenText ? "h" : "hours"} />
                    }
                    {isMinimized &&
                        <T.UnitMinimized>hrs</T.UnitMinimized>
                    }
                    <T.CountUp onClick={modifyBtn(inputState.h, onChangeHours, 1, "up")} minimized={isMinimized}>{countIcon("add")}</T.CountUp>
                    <T.CountDown onClick={modifyBtn(inputState.h, onChangeHours, 1, "down")} minimized={isMinimized}>{countIcon("minus")}</T.CountDown>
                </T.Column>

                {cleareable &&
                    <T.Btn miniDevice={miniDevice} minimized={isMinimized} onClick={onClear}>
                        <Button kind="transparentButton" icon="delete" onClick={onClear} tooltip={{ tooltipText: 'clear input' }} />
                    </T.Btn>
                }
            </T.Container>
        </div>
    );
}

export default DurationInputGetHours;
